import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { imgUrl } from "@/lib/api";
import { CATEGORIES, UNITS, STATUS_META, PRODUCTION_TAGS, SELF_PRODUCING_TYPES } from "@/lib/constants";
import ImageUpload from "@/components/ImageUpload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Loader2, Store, Package, ShoppingBag, Leaf, ExternalLink, CreditCard, Sparkles, AlertTriangle, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const empty = { image_url: "", name: "", category: "Veg", description: "", price: "", unit: "per kg", stock_quantity: 0, non_gmo: false, production_tags: [], sourced_from: "", available_from: "", available_until: "", active: true };

export default function SellerDashboard() {
  const navigate = useNavigate();
  const [farm, setFarm] = useState(null);
  const [sub, setSub] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const f = await api.get("/farms/mine");
      setFarm(f.data);
      if (f.data) {
        const [p, o, s] = await Promise.all([
          api.get("/products", { params: { farm_id: f.data.id, all: true } }),
          api.get("/orders/seller"),
          api.get("/subscription/me"),
        ]);
        setProducts(p.data);
        setOrders(o.data);
        setSub(s.data);
      }
    } catch (e) { /* */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditId(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (p) => { setEditId(p.id); setForm({ ...p, price: String(p.price) }); setDialogOpen(true); };

  const save = async () => {
    if (!form.image_url) { toast.error("Product image is required"); return; }
    if (!form.name || !form.price) { toast.error("Name and price are required"); return; }
    const needsSource = farm && !SELF_PRODUCING_TYPES.includes(farm.seller_type || "Farm");
    if (needsSource && !form.sourced_from.trim()) { toast.error("Please complete 'Sourced from' for this product."); return; }
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price), stock_quantity: Number(form.stock_quantity) || 0 };
      if (editId) await api.put(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      toast.success(editId ? "Product updated" : "Product added");
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/products/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const toggleActive = async (p) => {
    try { await api.put(`/products/${p.id}`, { ...p, active: !p.active }); load(); }
    catch { toast.error("Could not update"); }
  };

  const updateStatus = async (orderId, status) => {
    try { await api.put(`/orders/${orderId}/status`, { status }); toast.success("Order updated"); load(); }
    catch { toast.error("Could not update"); }
  };

  if (loading) return <div className="pt-32 text-center text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  if (!farm) {
    return (
      <div className="pt-16 min-h-screen grid place-items-center px-4">
        <Card className="rounded-2xl p-8 text-center max-w-md">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-secondary grid place-items-center mb-3"><Store className="h-7 w-7 text-primary" /></div>
          <h2 className="text-xl font-semibold">Set up your farm first</h2>
          <p className="text-muted-foreground text-sm mt-1">Register your farm to start listing products and receiving orders.</p>
          <Button className="mt-4 bg-[color:var(--fg-wood-600)] hover:bg-[color:var(--fg-wood-700)] text-white" onClick={() => navigate("/sell/register")} data-testid="dashboard-register-farm-button">Register your farm</Button>
        </Card>
      </div>
    );
  }

  const pendingCount = orders.filter((o) => o.status === "pending").length;

  return (
    <div className="pt-16 min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <SubscriptionBanner sub={sub} navigate={navigate} />
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-semibold">{farm.farm_name}</h1>
            <p className="text-muted-foreground text-sm">{farm.town} · {products.length} products · {orders.length} orders</p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => navigate("/sell/billing")} data-testid="dashboard-billing"><CreditCard className="h-4 w-4 mr-1" />Billing</Button>
            <Button variant="secondary" onClick={() => navigate(`/farms/${farm.id}`)} data-testid="dashboard-view-public"><ExternalLink className="h-4 w-4 mr-1" />View public page</Button>
            <Button variant="secondary" onClick={() => navigate("/sell/register")} data-testid="dashboard-edit-farm"><Pencil className="h-4 w-4 mr-1" />Edit farm</Button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6">
          <StatCard icon={<Package />} label="Products" value={products.length} />
          <StatCard icon={<ShoppingBag />} label="Pending orders" value={pendingCount} highlight={pendingCount > 0} />
          <StatCard icon={<Store />} label="Verified" value={farm.verified ? "Yes" : "No"} />
        </div>

        <Tabs defaultValue="products">
          <TabsList data-testid="seller-dashboard-nav">
            <TabsTrigger value="products" data-testid="dashboard-tab-products">Products</TabsTrigger>
            <TabsTrigger value="orders" data-testid="dashboard-tab-orders">Orders {pendingCount > 0 && <Badge className="ml-1.5 bg-[color:var(--fg-wood-600)] text-white">{pendingCount}</Badge>}</TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="mt-4">
            <div className="flex justify-end mb-3">
              <Button onClick={openAdd} data-testid="seller-dashboard-add-product-button"><Plus className="h-4 w-4 mr-1" />Add product</Button>
            </div>
            {products.length === 0 ? (
              <Card className="p-10 text-center text-muted-foreground rounded-2xl">No products yet. Add your first product.</Card>
            ) : (
              <Card className="rounded-2xl overflow-hidden">
                <Table data-testid="seller-products-table">
                  <TableHeader><TableRow>
                    <TableHead>Product</TableHead><TableHead>Category</TableHead><TableHead>Price</TableHead>
                    <TableHead>Stock</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {products.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell><div className="flex items-center gap-3">
                          {p.image_url ? <img src={imgUrl(p.image_url)} className="h-10 w-10 rounded-lg object-cover" alt="" /> : <div className="h-10 w-10 rounded-lg bg-secondary" />}
                          <div><div className="font-medium flex items-center gap-1">{p.name} {p.non_gmo && <Leaf className="h-3 w-3 text-[#3E5A1F]" />}</div><div className="text-xs text-muted-foreground">{p.unit}</div></div>
                        </div></TableCell>
                        <TableCell>{p.category}</TableCell>
                        <TableCell className="font-medium">£{p.price.toFixed(2)}</TableCell>
                        <TableCell>{p.stock_quantity}</TableCell>
                        <TableCell><Switch checked={p.active} onCheckedChange={() => toggleActive(p)} /></TableCell>
                        <TableCell className="text-right">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid="seller-product-edit-button"><Pencil className="h-4 w-4" /></Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild><Button size="icon" variant="ghost" data-testid="seller-product-delete-button"><Trash2 className="h-4 w-4 text-destructive" /></Button></AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Delete {p.name}?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(p.id)} data-testid="confirm-delete-product">Delete</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="orders" className="mt-4">
            {orders.length === 0 ? (
              <Card className="p-10 text-center text-muted-foreground rounded-2xl">No orders yet.</Card>
            ) : (
              <div className="space-y-3" data-testid="seller-orders-table">
                {orders.map((o) => (
                  <Card key={o.id} className="rounded-2xl p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">#{o.id.slice(0, 8)}</span>
                          <Badge className={STATUS_META[o.status]?.color}>{STATUS_META[o.status]?.label}</Badge>
                          <Badge variant="secondary">{o.fulfilment_type}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">{o.shopper_name} · {o.shopper_phone}</p>
                        <p className="text-sm text-muted-foreground">Fulfilment: {o.fulfilment_date ? new Date(o.fulfilment_date).toLocaleDateString("en-GB") : "—"}</p>
                        {o.fulfilment_type === "delivery" && o.delivery_address && (
                          <p className="text-sm text-primary font-medium mt-0.5">Deliver to: {o.delivery_address}</p>
                        )}
                        <ul className="text-sm mt-2 space-y-0.5">
                          {o.items.map((it) => <li key={it.id}>{it.quantity} × {it.product_name} <span className="text-muted-foreground">(£{it.price_at_order.toFixed(2)} {it.unit})</span></li>)}
                        </ul>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-lg">£{o.total_amount.toFixed(2)}</div>
                        <div className="text-xs text-muted-foreground mb-2">Pay on {o.fulfilment_type}</div>
                        <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                          <SelectTrigger className="w-[150px]" data-testid="seller-order-status-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {["pending", "confirmed", "ready", "collected", "cancelled"].map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Product dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit product" : "Add product"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Product image *</Label>
              <ImageUpload value={form.image_url} onChange={(u) => setForm({ ...form, image_url: u })} label="Upload product image" testid="product-image" /></div>
            <div className="space-y-1.5"><Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="product-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Unit</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger data-testid="product-unit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Price (£) *</Label>
                <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-price-input" /></div>
              <div className="space-y-1.5"><Label>Stock quantity</Label>
                <Input type="number" value={form.stock_quantity} onChange={(e) => setForm({ ...form, stock_quantity: e.target.value })} data-testid="product-stock-input" /></div>
            </div>
            <div className="space-y-1.5"><Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Available from</Label>
                <Input type="date" value={form.available_from} onChange={(e) => setForm({ ...form, available_from: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Available until</Label>
                <Input type="date" value={form.available_until} onChange={(e) => setForm({ ...form, available_until: e.target.value })} /></div>
            </div>
            <div className="space-y-2 rounded-lg border border-border p-3" data-testid="product-production-tags">
              <Label className="flex items-center gap-2"><Leaf className="h-4 w-4 text-[#3E5A1F]" /> Production details ({form.category})</Label>
              <p className="text-xs text-muted-foreground">Self-declared — shown as tags on your listing.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pt-1">
                {(PRODUCTION_TAGS[form.category] || []).map((tag) => {
                  const checked = (form.production_tags || []).includes(tag);
                  return (
                    <label key={tag} className={`flex items-center gap-2 rounded-md border p-2 text-sm cursor-pointer ${checked ? "border-primary bg-secondary" : "border-border"}`}>
                      <Checkbox checked={checked} data-testid="production-tag-checkbox"
                        onCheckedChange={(v) => setForm((f) => ({
                          ...f,
                          production_tags: v ? [...(f.production_tags || []), tag] : (f.production_tags || []).filter((t) => t !== tag),
                        }))} />
                      <span>{tag}</span>
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <Label>Active (visible to shoppers)</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="product-active-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="product-save-button">{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <button
        onClick={() => navigate("/sell/assistant")}
        title="Set up with the assistant"
        data-testid="dashboard-assistant-bubble"
        className="fixed bottom-6 right-6 z-[300] h-14 w-14 rounded-full bg-[color:var(--fg-wood-600)] hover:bg-[color:var(--fg-wood-700)] text-white shadow-lg grid place-items-center transition-transform hover:scale-105 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[color:var(--fg-wood-600)]"
      >
        <Sparkles className="h-6 w-6" />
      </button>
    </div>
  );
}

const StatCard = ({ icon, label, value, highlight }) => (
  <Card className={`rounded-2xl p-4 flex items-center gap-3 ${highlight ? 'border-[color:var(--fg-wood-600)] bg-[#F3EEE6]' : ''}`}>
    <span className="h-10 w-10 rounded-xl bg-secondary grid place-items-center text-primary">{icon}</span>
    <div><div className="text-2xl font-semibold leading-none">{value}</div><div className="text-xs text-muted-foreground mt-1">{label}</div></div>
  </Card>
);


const SubscriptionBanner = ({ sub, navigate }) => {
  if (!sub) return null;
  const status = sub.subscription_status;
  const fmt = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long" }) : "";

  if (status === "active") {
    return (
      <Card className="rounded-2xl p-4 mb-5 flex flex-wrap items-center justify-between gap-3 bg-emerald-50 border-emerald-200" data-testid="sub-banner-active">
        <div className="flex items-center gap-2 text-emerald-800 text-sm">
          <Sparkles className="h-4 w-4" /> <span>Your subscription is active{sub.next_billing_date ? ` · next billing ${fmt(sub.next_billing_date)}` : ""}. Your farm is live to shoppers.</span>
        </div>
        <Button size="sm" variant="secondary" onClick={() => navigate("/sell/billing")}>Manage billing</Button>
      </Card>
    );
  }

  if (status === "trialing") {
    const urgent = sub.trial_days_left <= 14;
    return (
      <Card className={`rounded-2xl p-4 mb-5 flex flex-wrap items-center justify-between gap-3 ${urgent ? "bg-amber-50 border-amber-200" : "bg-blue-50 border-blue-200"}`} data-testid="sub-banner-trial">
        <div className={`flex items-center gap-2 text-sm ${urgent ? "text-amber-900" : "text-blue-900"}`}>
          <CalendarClock className="h-4 w-4" />
          <span data-testid="trial-countdown">Your free trial ends in <span className="font-semibold">{sub.trial_days_left} days</span> ({fmt(sub.trial_ends_at)}).{urgent ? " Subscribe now to keep your listing active." : ""}</span>
        </div>
        <Button size="sm" className="bg-[color:var(--fg-wood-600)] hover:bg-[color:var(--fg-wood-700)] text-white" onClick={() => navigate("/sell/billing")} data-testid="sub-banner-subscribe">Subscribe · {sub.price_display}/mo</Button>
      </Card>
    );
  }

  // expired / cancelled / past_due -> listing hidden
  const msg = status === "past_due"
    ? "Your last payment failed and your listing is hidden from shoppers. Update your card to restore it."
    : "Your free trial has ended. Subscribe to make your farm visible again to shoppers.";
  return (
    <Card className="rounded-2xl p-4 mb-5 flex flex-wrap items-center justify-between gap-3 bg-red-50 border-red-200" data-testid="sub-banner-ended">
      <div className="flex items-start gap-2 text-red-800 text-sm">
        <AlertTriangle className="h-4 w-4 mt-0.5" />
        <span>{msg} <span className="text-red-600">Your data and products are safe.</span></span>
      </div>
      <Button size="sm" className="bg-[color:var(--fg-wood-600)] hover:bg-[color:var(--fg-wood-700)] text-white" onClick={() => navigate("/sell/billing")} data-testid="sub-banner-reactivate">
        {status === "past_due" ? "Update payment" : "Subscribe · " + sub.price_display + "/mo"}
      </Button>
    </Card>
  );
};
